import { config } from "../config";
import { getToken } from "./auth";
import { Sop, RecordingMetadata } from "../types/sop";

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = await getToken();
  const res = await fetch(`${config.apiUrl}${path}`, {
    ...options,
    headers: { Authorization: token, "Content-Type": "application/json", ...options.headers },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function getUploadUrl(meta: RecordingMetadata & { file_ext?: string }) {
  return apiFetch("/upload-url", {
    method: "POST",
    body: JSON.stringify({ station_id: meta.station_id, role: meta.role, file_ext: meta.file_ext ?? "webm" }),
  }) as Promise<{ upload_url: string; audio_key: string }>;
}

export async function uploadAudio(url: string, blob: Blob) {
  await fetch(url, { method: "PUT", body: blob, headers: { "Content-Type": blob.type || "application/octet-stream" } });
}

export async function startTranscription(audioKey: string, meta: RecordingMetadata) {
  return apiFetch("/recordings", {
    method: "POST",
    body: JSON.stringify({ audio_key: audioKey, ...meta, ...(meta.submitted_by ? { submitted_by: meta.submitted_by } : {}) }),
  }) as Promise<{ job_id: string; status: string }>;
}

export async function listSops(stationId?: string) {
  const qs = stationId ? `?station_id=${stationId}` : "";
  return apiFetch(`/sops${qs}`) as Promise<Sop[]>;
}

export async function reviewSop(sopId: string, stationId: string, action: "approved" | "rejected", edits?: Partial<Sop>) {
  return apiFetch(`/sops/${sopId}/review`, {
    method: "POST",
    body: JSON.stringify({ station_id: stationId, action, edits }),
  });
}

export interface SearchResult {
  sop_id: string;
  station_id: string;
  title: string;
  purpose: string;
  process_area: string;
  target_role: string;
  quick_reference: string;
  score: number;
}

export async function searchSops(params: {
  q?: string;
  process_area?: string;
  role?: string;
  station_id?: string;
}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][]
  ).toString();
  return apiFetch(`/search?${qs}`) as Promise<{ results: SearchResult[]; total: number }>;
}

export async function trackEvent(payload: {
  sop_id: string;
  station_id: string;
  event_type: "view" | "adopt" | "unadopt" | "share";
  actor_station?: string;
  actor_role?: string;
  source_station?: string;
}) {
  return apiFetch("/events", { method: "POST", body: JSON.stringify(payload) });
}

export interface TopSop {
  sop_id: string;
  station_id: string;
  title: string;
  process_area: string;
  adoption_count: number;
}

export interface SopAnalytics {
  sop_id: string;
  views_total: number;
  views_last_7d: number;
  adoptions: number;
  shares: number;
  adopting_stations: string[];
}

export async function getAnalytics(report: string, params?: Record<string, string>) {
  const qs = new URLSearchParams({ report, ...params }).toString();
  return apiFetch(`/analytics?${qs}`);
}

export async function exportSop(sopId: string, stationId: string) {
  return apiFetch(`/export?sop_id=${sopId}&station_id=${stationId}`) as Promise<{ download_url: string }>;
}

export async function ingestMetrics(payload: {
  station_id: string;
  period: string;
  metrics: Record<string, number>;
}) {
  return apiFetch("/metrics", { method: "POST", body: JSON.stringify(payload) });
}

export interface CorrelationResult {
  sop_id: string;
  metric: string;
  adopters: { count: number; avg: number | null; stations: string[] };
  non_adopters: { count: number; avg: number | null };
}

export interface StationCompare {
  station_id: string;
  metrics_history: Record<string, any>[];
  adoptions: { sop_id: string; timestamp: number }[];
}

export async function getCorrelation(report: string, params?: Record<string, string>) {
  const qs = new URLSearchParams({ report, ...params }).toString();
  return apiFetch(`/correlate?${qs}`);
}
